// src/components/CardTotalProposalIssued.js (adjust path as needed)
import React, { useEffect, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import {
    Card,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
} from '@sunwisesoftware/sunwise-ui';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { StyledH2 } from 'common/components';
import { formatDate, subDate } from 'common/utils/dates';
import { numberFormat } from 'common/utils/helpers';

import * as actions from '../actions';
import * as selectors from '../selectors';

import DateFilterForm from './DateFilterForm';

const fixMarginBottom = {
    '& .MuiFormControl-root, & .MuiButton-root': { marginBottom: 0 },
};

const CardTotalProposalIssued = ({
    fetchProposalsTotals,
    installerId,
    initialValues,
    proposalsTotals,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [chartType, setChartType] = useState('total');

    useEffect(() => {
        const initialDate = formatDate(
            subDate(new Date(), { years: 1 }),
            'dd/MM/yyyy',
        );
        const finalDate = formatDate(new Date(), 'dd/MM/yyyy');
        const values = {
            final_date: finalDate,
            initial_date: initialDate,
        };
        fetchProposalsTotals(values, installerId);
    }, [installerId]);

    const handleChangeChartOptions = (type) => setChartType(type);

    const getSeriesData = (data) => {
        return [
            {
                data: data.map((item) => get(item, `${chartType}_emitida`, 0)),
                name: t('Proposals issued without your products'),
                type: 'column',
            },
            {
                data: data.map((item) => get(item, `${chartType}_cotizada`, 0)),
                name: t('Proposals issued with your products'),
                type: 'column',
            },
            {
                data: data.map((item) =>
                    get(item, `${chartType}_solicitada`, 0),
                ),
                name: t('Proposals issued and requested'),
                type: 'column',
            },
        ];
    };

    const chartConfig = {
        options: {
            chart: { background: '#ffffff00', toolbar: { show: false } },
            colors: ['#03e396', '#0073d0', '#346eeb'],
            dataLabels: { enabled: false },
            legend: { show: true },
            xaxis: { categories: proposalsTotals.categories },
            yaxis: [
                {
                    axisBorder: { show: true },
                    decimalsInFloat: 0,
                    labels: {
                        formatter: (val) =>
                            chartType === 'total'
                                ? val
                                : chartType === 'kw_propuesta'
                                  ? numberFormat(val, {
                                        style: 'decimal',
                                        unit: 'kW',
                                    })
                                  : numberFormat(val, {
                                        style: 'currency',
                                    }),
                    },
                },
            ],
        },
        series: getSeriesData(
            Object.keys(proposalsTotals.series).map(
                (serieKey) => proposalsTotals.series[serieKey],
            ),
        ),
    };

    const handleFilterSubmit = (values) =>
        fetchProposalsTotals(values, installerId);

    return (
        <Card>
            <Card.Header>
                <Grid alignItems="center" container sx={fixMarginBottom}>
                    <Grid size={{ md: 9, xs: 18 }}>
                        <StyledH2>{t('Total proposals')}</StyledH2>
                    </Grid>

                    <Grid size={{ md: 9, xs: 18 }}>
                        <DateFilterForm
                            initialValues={initialValues}
                            onSubmit={handleFilterSubmit}
                        />
                    </Grid>
                </Grid>
            </Card.Header>

            <Card.Body>
                <ToggleButtonGroup
                    color="primary"
                    exclusive
                    fullWidth
                    onChange={(e) => handleChangeChartOptions(e.target.value)}
                    size="small"
                    sx={{ mb: 2 }}
                    value={chartType}
                >
                    <ToggleButton value="total">{t('Total')}</ToggleButton>
                    <ToggleButton value="kw_propuesta">
                        {t('Power')}
                    </ToggleButton>
                    <ToggleButton value="total_propuesta">
                        {t('Worth')}
                    </ToggleButton>
                </ToggleButtonGroup>

                <Chart
                    height="250"
                    id={`chart-${chartType}`}
                    options={{
                        ...chartConfig.options,
                        theme: { mode: theme.palette.mode },
                    }}
                    series={chartConfig.series}
                    type="bar"
                />
            </Card.Body>
        </Card>
    );
};

CardTotalProposalIssued.propTypes = {
    fetchProposalsTotals: PropTypes.func,
    installerId: PropTypes.string,
    initialValues: PropTypes.object,
    proposalsTotals: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
});

const mapDispatchToProps = (dispatch) => ({
    fetchProjectsSalesStage: () => dispatch(actions.fetchProjectsSalesStage()),
    fetchProposalsTotals: (values, installerId) =>
        dispatch(actions.fetchProposalsTotals(values, installerId)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(CardTotalProposalIssued);
